import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { SIGNED_OUT_EVENT } from "./api/client";
import { keys, useAuth } from "./api/hooks";
import { Layout, Loading } from "./components/Layout";
import { DoilsPage } from "./pages/DoilsPage";
import { HoursPage } from "./pages/HoursPage";
import { ImportPage } from "./pages/ImportPage";
import { JobPage } from "./pages/JobPage";
import { JobsPage } from "./pages/JobsPage";
import { AdminPage } from "./pages/AdminPage";
import { LoginPage } from "./pages/LoginPage";
import { OverviewPage } from "./pages/OverviewPage";
import { PlannerPage } from "./pages/PlannerPage";
import { ResetPage } from "./pages/ResetPage";
import { SettingsPage } from "./pages/SettingsPage";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const location = useLocation();
  if (auth.isPending)
    return (
      <main>
        <Loading />
      </main>
    );
  if (auth.data && (!auth.data.authenticated || !auth.data.user)) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  }
  return <>{children}</>;
}

export default function App() {
  const qc = useQueryClient();

  // Any 401 from the API means the session has expired: re-check sign-in state.
  useEffect(() => {
    const onSignedOut = () => qc.invalidateQueries({ queryKey: keys.auth });
    window.addEventListener(SIGNED_OUT_EVENT, onSignedOut);
    return () => window.removeEventListener(SIGNED_OUT_EVENT, onSignedOut);
  }, [qc]);

  const thisYear = new Date().getFullYear();
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset/:token" element={<ResetPage />} />
      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to={`/year/${thisYear}/overview`} replace />} />
        <Route path="/year/:year/overview" element={<OverviewPage />} />
        <Route path="/year/:year" element={<PlannerPage />} />
        <Route path="/year/:year/jobs" element={<JobsPage />} />
        <Route path="/year/:year/hours" element={<HoursPage />} />
        <Route path="/year/:year/doils" element={<DoilsPage />} />
        <Route path="/year/:year/settings" element={<SettingsPage />} />
        <Route path="/job/:id" element={<JobPage />} />
        <Route path="/import" element={<ImportPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="*" element={<p>Page not found.</p>} />
      </Route>
    </Routes>
  );
}
